import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    this.#bindCardArrows();
    this.#updateArrowVisibility();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#unbindCardArrows();
  }

  /** @type {HTMLElement | null} */
  #nextArrow = null;

  /** @type {HTMLElement | null} */
  #previousArrow = null;

  /**
   * Attaches real click listeners to the colour-filtered gallery arrows.
   *
   * These are bound directly rather than through the on:click="product-card/method" delegation:
   * calling the same methods from the console moved the gallery correctly while clicking the
   * delegated buttons did not, so the binding is made explicit here.
   */
  #bindCardArrows() {
    this.#unbindCardArrows();

    this.#nextArrow = this.querySelector('[data-card-arrow="next"]');
    this.#previousArrow = this.querySelector('[data-card-arrow="previous"]');

    this.#nextArrow?.addEventListener('click', this.nextColorSlide);
    this.#previousArrow?.addEventListener('click', this.previousColorSlide);
  }

  #unbindCardArrows() {
    this.#nextArrow?.removeEventListener('click', this.nextColorSlide);
    this.#previousArrow?.removeEventListener('click', this.previousColorSlide);
    this.#nextArrow = null;
    this.#previousArrow = null;
  }

  /**
   * Shows or hides the arrows to match the selected colour.
   *
   * The markup is rendered whenever any colour has more than one photo, because Liquid only knows
   * the colour selected at render time and colours are switched client-side afterwards. A colour
   * with a single photo has nothing to page through, so the arrows are hidden for it here.
   */
  #updateArrowVisibility() {
    const arrows = this.querySelector('[data-card-arrows]');

    if (!(arrows instanceof HTMLElement)) return;

    arrows.hidden = this.#getVisibleSlides().length <= 1;
  }

  /**
   * Resizes the card to match the photo actually on screen.
   *
   * With image_ratio 'adapt', snippets/card-gallery.liquid sets --gallery-aspect-ratio from the
   * first media that survives the colour filter. That is only correct for the colour the page was
   * rendered with: colours are switched client-side without a server re-render, so once the
   * shopper picks a different swatch the box can be left shaped for the previous colour's photo.
   * Slides use object-fit: cover, so a stale ratio crops and zooms the new photo instead of
   * showing it whole.
   *
   * Each slide carries its own data-aspect-ratio for this reason. Only applied when the block is
   * set to 'adapt' -- the fixed portrait/square/landscape ratios are meant to stay put.
   *
   * @param {HTMLElement | undefined} slide - The slide that is becoming visible.
   */
  #updateGalleryAspectRatio(slide) {
    const { cardGallery } = this.refs;

    if (!(cardGallery instanceof HTMLElement) || !slide) return;
    if (cardGallery.dataset.imageRatio !== 'adapt') return;

    const ratio = slide.getAttribute('data-aspect-ratio');

    if (ratio) cardGallery.style.setProperty('--gallery-aspect-ratio', ratio);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    // A new colour starts a fresh gallery, so any manual paging in the previous colour is forgotten.
    this.#userPaged = false;

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // The picker markup may have been morphed, so re-attach the arrow listeners.
    this.#bindCardArrows();

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the gallery media that does not belong to the selected colour.
   *
   * The card gallery is rendered once and never re-rendered on swatch change, so every colour's
   * media is present in the DOM and filtered here. Cards use a strict match: only the selected
   * colour's photos are shown. Untagged lifestyle shots (data-color="none") are excluded, unlike
   * the product page gallery, and are used only as a fallback when a colour has no photos at all.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    const selectedOption = this.variantPicker?.selectedOption;

    if (!slideshow || !selectedOption) return;

    const selectedColor = selectedOption.dataset.optionColor;
    const selectedImageId = selectedOption.dataset.optionMediaId;
    const slides = Array.from(slideshow.refs?.slides || []);

    if (selectedColor && slides.some((slide) => slide.hasAttribute('data-color'))) {
      let firstVisibleId = null;

      for (const slide of slides) {
        const slideColor = slide.getAttribute('data-color');
        const matches = slideColor === selectedColor;

        slide.hidden = !matches;
        // Slideshow.select() reveals any hidden slide it is asked for by setting this attribute,
        // which would put another colour's slide back into rotation. Clear it on every pass.
        if (!matches) slide.removeAttribute('reveal');

        if (matches && firstVisibleId == null) {
          firstVisibleId = slide.getAttribute('slide-id');
        }
      }

      // Fallback: if this colour has no photos of its own, show the untagged shots rather than
      // rendering an empty card. Covers colours that exist as options but were never shot.
      if (firstVisibleId == null) {
        for (const slide of slides) {
          const slideColor = slide.getAttribute('data-color');
          const isShared = slideColor == null || slideColor === 'none';

          slide.hidden = !isShared;
          if (!isShared) slide.removeAttribute('reveal');

          if (isShared && firstVisibleId == null) {
            firstVisibleId = slide.getAttribute('slide-id');
          }
        }
      }

      // Last resort: nothing matched at all, so unhide everything.
      if (firstVisibleId == null) {
        for (const slide of slides) {
          slide.hidden = false;
        }
        firstVisibleId = slides[0]?.getAttribute('slide-id') ?? null;
      }

      // The number of photos differs per colour, so the arrows may need to appear or disappear.
      this.#updateArrowVisibility();

      // Prefer the variant's own hero image, but only if it survived the filter.
      const heroIsVisible = slides.some(
        (slide) => slide.getAttribute('slide-id') === selectedImageId && !slide.hidden
      );
      const targetId = heroIsVisible ? selectedImageId : firstVisibleId;

      if (targetId) {
        // Re-seed the tracked position for the new colour before moving.
        const visible = slides.filter((slide) => !slide.hidden);
        const targetIndex = visible.findIndex((slide) => slide.getAttribute('slide-id') === targetId);
        this.#colorSlideIndex = targetIndex >= 0 ? targetIndex : 0;

        // Reshape the card to the incoming photo before it is scrolled into view, so the change of
        // colour and the change of box happen in the same frame rather than as a visible reflow.
        this.#updateGalleryAspectRatio(visible[this.#colorSlideIndex]);

        // Wait a frame: the slides were just hidden, and the scroller needs to reflow before its
        // geometry can be measured.
        requestAnimationFrame(() => this.#scrollToSlide(visible[this.#colorSlideIndex], false));
      }

      return;
    }

    // Fallback for galleries without colour tags: original variant-image behaviour.
    if (selectedImageId) {
      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Whether the shopper has paged the gallery manually with the arrows.
   * While true, the hover handlers leave the gallery alone so a deliberate choice is not
   * overwritten by moving the mouse.
   * @type {boolean}
   */
  #userPaged = false;

  /**
   * Our own position within the currently visible (colour-filtered) slides.
   *
   * Tracked rather than derived from scrollLeft, which is unreliable to read while a scroll
   * animation is still running.
   *
   * @type {number}
   */
  #colorSlideIndex = 0;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;

      // Keep the tracked index in step when the position changes by some other means,
      // for example a swipe or drag on touch devices.
      const visible = this.#getVisibleSlides();
      const index = visible.indexOf(event.detail.slide);

      if (index >= 0) this.#colorSlideIndex = index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    const slides = Array.from(slideshow.refs?.slides || []);
    const target = slides.find((slide) => slide.getAttribute('slide-id') === String(id));

    if (target?.hidden) {
      target.hidden = false;
    }

    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Returns the slideshow slides in a version-safe way.
   * @returns {HTMLElement[]}
   */
  #getSlideshowSlides() {
    const { slideshow } = this.refs;
    if (!slideshow) return [];

    const slides = slideshow.refs?.slides || slideshow.slides || [];
    return Array.from(slides);
  }

  /**
   * Returns only the slides left visible by the colour filter.
   * @returns {HTMLElement[]}
   */
  #getVisibleSlides() {
    return this.#getSlideshowSlides().filter((slide) => !slide.hidden);
  }

  /**
   * Moves the gallery to a slide by scrolling its container directly.
   *
   * Slideshow.select() is deliberately NOT used for card paging. On a jump it is not treated as
   * adjacent, it sets an internal disabled flag and physically reorders slides in the DOM until an
   * async restore completes. While that flag is set every later select() call returns immediately,
   * and the reordering invalidates the slide positions tracked here.
   *
   * @param {HTMLElement | undefined} slide
   * @param {boolean} animate
   */
  #scrollToSlide(slide, animate = false) {
    if (!slide) return;

    const scroller = slide.parentElement;

    if (scroller && scroller.scrollWidth > scroller.clientWidth) {
      // Measured with rects rather than offsetLeft, which is relative to the nearest positioned
      // ancestor and would mix coordinate spaces.
      const left = scroller.scrollLeft + slide.getBoundingClientRect().left - scroller.getBoundingClientRect().left;

      scroller.scrollTo({ left, behavior: animate ? 'smooth' : 'auto' });
      return;
    }

    // Fallback for a gallery that does not overflow (single visible slide, or a stacked layout).
    const { slideshow } = this.refs;
    const slideId = slide.getAttribute('slide-id');

    if (slideshow && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
    }
  }

  /**
   * Steps through the visible slides, wrapping within that set.
   * @param {number} step
   * @param {boolean} animate
   */
  #stepSlide(step, animate = false) {
    const visible = this.#getVisibleSlides();

    if (!visible.length) return;

    // Clamp first: the visible set changes when the colour changes.
    const from = Math.min(Math.max(this.#colorSlideIndex, 0), visible.length - 1);
    const next = ((from + step) % visible.length + visible.length) % visible.length;

    this.#colorSlideIndex = next;
    this.#scrollToSlide(visible[next], animate);
  }

  /**
   * Card gallery arrow: advance within the selected colour only.
   *
   * Defined as a bound field so it can be attached and detached as a click listener directly.
   *
   * @param {Event} [event]
   */
  nextColorSlide = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    this.resetVariant.cancel();
    this.#userPaged = true;
    this.#stepSlide(1, true);
  };

  /**
   * Card gallery arrow: go back within the selected colour only.
   * @param {Event} [event]
   */
  previousColorSlide = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    this.resetVariant.cancel();
    this.#userPaged = true;
    this.#stepSlide(-1, true);
  };

  /**
   * Previews the next image on hover.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    // Don't auto-advance over a slide the shopper chose with the arrows.
    if (this.#userPaged) return;

    this.resetVariant.cancel();

    this.#stepSlide(1, false);
    setTimeout(() => this.#preloadNextPreviewImage());
  }

  /**
   * Resets the image when the pointer leaves the card.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    // Keep the slide the shopper paged to instead of snapping back to the first image.
    // Cleared here so the next hover behaves normally again.
    if (this.#userPaged) {
      this.#userPaged = false;
      return;
    }

    if (!this.variantPicker) {
      if (!slideshow) return;
      this.#stepSlide(-1, false);
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // Re-apply the colour filter first: anything that revealed a hidden slide must be undone.
    if (this.variantPicker?.selectedOption?.dataset.optionColor) {
      this.#updateVariantImages();
      return;
    }

    // If we have a selected variant, always use its image.
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;

      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - go back to the first visible slide.
    const visible = this.#getVisibleSlides();

    this.#colorSlideIndex = 0;
    this.#scrollToSlide(visible[0], false);
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
